import { ref, type Ref } from "vue";

/**
 * Composable d'état d'expand des cellules d'un tableau de données.
 *
 * <p><b>Trois états réactifs</b> qui se combinent par OR-rule avec
 * override négatif :
 * <ul>
 *   <li>{@code globalExpand} ( booléen Tier 3 ) : quand vrai , toute
 *       cellule est considérée dépliée par défaut , sauf override
 *       individuel.</li>
 *   <li>{@code expandedCells} ( Set Tier 1 + Tier 4 ) : cellules
 *       dépliées individuellement ( par toggleCell ) ou en bulk
 *       colonne ( par toggleColumn ). Ignoré quand globalExpand
 *       est actif.</li>
 *   <li>{@code forceCollapsedCells} ( Set ) : override négatif
 *       utilisé UNIQUEMENT quand globalExpand est actif , pour
 *       qu'un click toggleCell puisse replier individuellement
 *       une cellule sans désactiver le mode global.</li>
 * </ul>
 *
 * <p><b>Affichage</b> : la troncature visuelle ( ellipse 1 ligne ou
 * wrap multi-ligne ) est gérée côté CSS par
 * {@code <TruncatableCell>}. Le composable ne touche JAMAIS au
 * texte ; il ne fait que piloter l'état d'expand par cellule.
 *
 * <p><b>Bulk-toggles filtrés par longueur</b> : toggleColumn
 * n'opère que sur les cellules dont le texte dépasse {@code maxChars}.
 * Les cellules courtes - dont le texte tient déjà dans la colonne -
 * ne sont pas affectées par un click sur le ▾ d'un header de colonne :
 * il n'y a rien à déplier , les altérer ne ferait que rétracter la
 * colonne. Tier 3 ( globalExpand ) , lui , déplie tout par défaut
 * mais l'effet visuel sur les cellules courtes est neutre car
 * <TruncatableCell> n'applique son CSS expand qu'aux cellules longues.
 */
export interface UseCellExpandOptions<TRow, TCol> {
  /** Identifiant stable d'une ligne ( de préférence sa naturalKey ). */
  getRowKey: (row: TRow) => string;
  /** Identifiant stable d'une colonne. */
  getColKey: (col: TCol) => string;
  /**
   * Texte rendu dans la cellule ( ce que voit l'utilisateur ). Doit
   * refléter le label human-readable - pas la clé technique.
   */
  getCellText: (row: TRow, col: TCol) => string;
  /**
   * Seuil au-delà duquel une cellule est considérée "longue" et
   * éligible aux bulk-toggles colonne ( toggleColumn ). Les cellules
   * courtes ( text.length ≤ maxChars ) sont ignorées par toggleColumn :
   * leur texte tient déjà visuellement , il n'y a rien à déplier , et
   * les altérer ferait rétracter la colonne sans bénéfice utilisateur.
   * Default {@link DEFAULT_MAX_CHARS} ( 80 ) , à aligner avec la prop
   * maxChars du composant {@code <TruncatableCell>}.
   */
  maxChars?: number;
}

/**
 * Seuil par défaut : 80 chars / ligne ( convention typographique
 * proche du standard "80 colonnes" historique ). Compromis entre
 * lisibilité et densité du tableau.
 */
const DEFAULT_MAX_CHARS = 80;

export interface UseCellExpand<TRow, TCol> {
  /** Texte brut à passer à <TruncatableCell> via la prop text. */
  cellRawText: (row: TRow, col: TCol) => string;
  /** Vrai si la cellule est dépliée ( OR de Tier 3 et Tier 1+4 ). */
  isCellExpanded: (row: TRow, col: TCol) => boolean;
  /** Toggle Tier 1 ( cellule unique ). */
  toggleCell: (row: TRow, col: TCol) => void;
  /** Vrai si toutes les cellules longues de la colonne sont dépliées. */
  isColumnAllExpanded: (col: TCol, rows: ReadonlyArray<TRow>) => boolean;
  /** Bulk-toggle Tier 4 ( toutes les cellules longues d'une colonne ). */
  toggleColumn: (col: TCol, rows: ReadonlyArray<TRow>) => void;
  /** Vrai si le mode global "tout déplier" est activé ( Tier 3 ). */
  isAllExpanded: () => boolean;
  /** Toggle Tier 3 ( global ). Quand on désactive , on reset aussi
   *  les expansions Tier 1 / 4 individuelles pour que "Tout replier"
   *  replie vraiment tout. */
  toggleAll: () => void;
  /** Set réactif sous-jacent ( exposé pour les tests ). */
  expandedCells: Ref<Set<string>>;
  /** Flag réactif Tier 3 ( exposé pour les tests ). */
  globalExpand: Ref<boolean>;
}

export function useCellExpand<TRow, TCol>(
  opts: UseCellExpandOptions<TRow, TCol>,
): UseCellExpand<TRow, TCol> {
  const maxChars = opts.maxChars ?? DEFAULT_MAX_CHARS;

  /**
   * Tier 3 - flag global : quand vrai , TOUTES les cellules sont
   * considérées dépliées par défaut.
   */
  const globalExpand = ref(false);

  /**
   * Tier 1 + Tier 4 - Set par cellule : alimenté par toggleCell
   * ( cellule individuelle ) et toggleColumn ( bulk colonne ).
   * Combiné avec globalExpand via OR.
   */
  const expandedCells = ref<Set<string>>(new Set());

  /**
   * Override négatif : cellules explicitement repliées par
   * l'utilisateur PENDANT que globalExpand est actif. Sans ça ,
   * cliquer le ▾ d'une cellule en mode "Tout déplier" n'aurait
   * aucun effet visible ( globalExpand l'override ). On stocke ici
   * les cellules à forcer en repli même si globalExpand=true.
   * Reset par toggleAll.
   */
  const forceCollapsedCells = ref<Set<string>>(new Set());

  function cellKey(row: TRow, col: TCol): string {
    return `${opts.getRowKey(row)}::${opts.getColKey(col)}`;
  }

  function cellRawText(row: TRow, col: TCol): string {
    return opts.getCellText(row, col);
  }

  function isLong(row: TRow, col: TCol): boolean {
    return opts.getCellText(row, col).length > maxChars;
  }

  function isCellExpanded(row: TRow, col: TCol): boolean {
    const k = cellKey(row, col);
    if (globalExpand.value) {
      // Mode global : dépliée par défaut , sauf si l'utilisateur l'a
      // explicitement repliée individuellement ( override négatif ).
      return !forceCollapsedCells.value.has(k);
    }
    return expandedCells.value.has(k);
  }

  function toggleCell(row: TRow, col: TCol): void {
    const k = cellKey(row, col);
    if (globalExpand.value) {
      // Mode global ON : on bascule l'OVERRIDE négatif. Si la cellule
      // était dépliée ( par globalExpand ) , on l'ajoute au set des
      // forçages-repli ; si déjà dans le set , on retire ( elle
      // redevient dépliée par globalExpand ).
      const next = new Set(forceCollapsedCells.value);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      forceCollapsedCells.value = next;
      return;
    }
    // Mode normal : on bascule la clé dans expandedCells.
    const next = new Set(expandedCells.value);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    expandedCells.value = next;
  }

  function keysForColumn(col: TCol, rows: ReadonlyArray<TRow>): string[] {
    return rows
      .filter(r => isLong(r, col))
      .map(r => cellKey(r, col));
  }

  function isColumnAllExpanded(col: TCol, rows: ReadonlyArray<TRow>): boolean {
    if (globalExpand.value) return true;
    const keys = keysForColumn(col, rows);
    if (keys.length === 0) return false;
    return keys.every(k => expandedCells.value.has(k));
  }

  function toggleColumn(col: TCol, rows: ReadonlyArray<TRow>): void {
    const keys = keysForColumn(col, rows);
    if (keys.length === 0) return;
    const allExpanded = keys.every(k => expandedCells.value.has(k));
    const next = new Set(expandedCells.value);
    if (allExpanded) keys.forEach(k => next.delete(k));
    else keys.forEach(k => next.add(k));
    expandedCells.value = next;
  }

  function isAllExpanded(): boolean {
    return globalExpand.value;
  }

  function toggleAll(): void {
    if (globalExpand.value) {
      // Tier 3 OFF : "Tout replier" doit vraiment tout replier , y
      // compris les expansions Tier 1 / 4 individuelles ET l'override
      // négatif ( forceCollapsedCells devient sans objet quand le
      // mode global est OFF ).
      globalExpand.value = false;
      expandedCells.value = new Set();
      forceCollapsedCells.value = new Set();
    }
    else {
      globalExpand.value = true;
      // En activant le mode global , on repart d'un override propre :
      // l'utilisateur peut ensuite replier individuellement les cellules
      // qu'il veut.
      forceCollapsedCells.value = new Set();
    }
  }

  return {
    cellRawText,
    isCellExpanded,
    toggleCell,
    isColumnAllExpanded,
    toggleColumn,
    isAllExpanded,
    toggleAll,
    expandedCells,
    globalExpand,
  };
}
